import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Plus, Trash, PencilSimple, EnvelopeSimple, FilePdf, ArrowClockwise, Warning } from '@phosphor-icons/react';

/**
 * Collections Panel (Phase 900)
 * ------------------------------------------------------------------
 * A fully self-contained UI for the Collections module. Renders under
 * a new "Collections" tab inside BuildingDetails. Every request is
 * scoped to `buildingId`; nothing here touches other modules.
 */

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUSES = [
  'open', 'friendly_reminder_sent', 'first_reminder_sent', 'final_notice_sent',
  'pending_committee_review', 'legal_action', 'resolved', 'closed',
];

const STATUS_LABEL_EL = {
  open: 'Ανοιχτό',
  friendly_reminder_sent: 'Φιλική Υπενθύμιση',
  first_reminder_sent: 'Πρώτη Υπενθύμιση',
  final_notice_sent: 'Τελική Ειδοποίηση',
  pending_committee_review: 'Επιτροπή',
  legal_action: 'Νομική Ενέργεια',
  resolved: 'Επιλύθηκε',
  closed: 'Κλειστό',
};

const STATUS_BADGE_COLOR = {
  open: 'bg-zinc-100 text-zinc-800',
  friendly_reminder_sent: 'bg-sky-50 text-sky-800',
  first_reminder_sent: 'bg-amber-50 text-amber-800',
  final_notice_sent: 'bg-orange-50 text-orange-800',
  pending_committee_review: 'bg-purple-50 text-purple-800',
  legal_action: 'bg-red-50 text-red-800',
  resolved: 'bg-emerald-50 text-emerald-800',
  closed: 'bg-zinc-100 text-zinc-500',
};

export default function CollectionsPanel({ buildingId, apartments = [], language = 'en', isAdmin = false }) {
  const [tab, setTab] = useState('cases'); // 'cases' | 'templates'
  const [cases, setCases] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedCase, setSelectedCase] = useState(null); // detail modal
  const [history, setHistory] = useState([]);
  const [caseDetail, setCaseDetail] = useState(null); // full detail with overdue
  const [newCaseAptId, setNewCaseAptId] = useState('');
  const [sendDialog, setSendDialog] = useState(null); // {mode: 'email'|'pdf', tplId, subject, body}
  const [tplDialog, setTplDialog] = useState(null);

  const isEl = language === 'el';
  const label = (el, en) => (isEl ? el : en);

  const reload = async () => {
    setLoading(true);
    try {
      const [cRes, tRes] = await Promise.all([
        axios.get(`${API}/buildings/${buildingId}/collections`),
        axios.get(`${API}/buildings/${buildingId}/collections/templates`),
      ]);
      setCases(cRes.data.cases || []);
      setTemplates(tRes.data.templates || []);
    } catch (e) {
      // silent — module may not be initialised on server startup for this building
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (buildingId) reload(); /* eslint-disable-next-line */ }, [buildingId]);

  // ---- Case actions ----
  const openCase = async (aptId) => {
    if (!aptId) return;
    try {
      const res = await axios.post(`${API}/buildings/${buildingId}/collections`, { apartment_id: aptId });
      toast.success(label('Δημιουργήθηκε case', 'Case opened'));
      setNewCaseAptId('');
      reload();
      openDetail(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed');
    }
  };

  const openDetail = async (c) => {
    setSelectedCase(c);
    try {
      const [detailRes, histRes] = await Promise.all([
        axios.get(`${API}/buildings/${buildingId}/collections/${c.case_id}`),
        axios.get(`${API}/buildings/${buildingId}/collections/${c.case_id}/history`),
      ]);
      setCaseDetail(detailRes.data);
      setHistory(histRes.data.entries || []);
    } catch (e) { toast.error('Failed to load'); }
  };

  const closeDetail = () => { setSelectedCase(null); setCaseDetail(null); setHistory([]); };

  const updateStatus = async (newStatus) => {
    if (!caseDetail) return;
    try {
      await axios.put(`${API}/buildings/${buildingId}/collections/${caseDetail.case_id}`, { status: newStatus });
      toast.success(label('Ενημερώθηκε', 'Updated'));
      openDetail(caseDetail);
      reload();
    } catch (e) { toast.error('Update failed'); }
  };

  const addNote = async (text) => {
    if (!text.trim()) return;
    try {
      await axios.post(`${API}/buildings/${buildingId}/collections/${caseDetail.case_id}/history`, {
        entry_type: 'note', subject: '', body: text.trim(),
      });
      openDetail(caseDetail);
    } catch (e) { toast.error('Note failed'); }
  };

  const deleteCase = async (c) => {
    if (!window.confirm(label('Κλείσιμο case;', 'Close this case?'))) return;
    try {
      await axios.delete(`${API}/buildings/${buildingId}/collections/${c.case_id}`);
      toast.success(label('Έκλεισε', 'Closed'));
      reload();
      if (selectedCase?.case_id === c.case_id) closeDetail();
    } catch (e) { toast.error('Delete failed'); }
  };

  // ---- Send / PDF ----
  const openSendDialog = (mode) => setSendDialog({ mode, tplId: '', subject: '', body: '' });

  const runSend = async () => {
    if (!sendDialog) return;
    const body = { template_id: sendDialog.tplId || undefined };
    if (sendDialog.subject) body.subject = sendDialog.subject;
    if (sendDialog.body) body.body = sendDialog.body;
    try {
      if (sendDialog.mode === 'email') {
        const r = await axios.post(`${API}/buildings/${buildingId}/collections/${caseDetail.case_id}/send-email`, body);
        const st = r?.data?.status;
        if (st === 'sent') toast.success(label('Στάλθηκε', 'Email sent'));
        else if (st === 'skipped_testing') toast.warning(label('Kill-switch — δεν στάλθηκε', 'Kill-switch — not sent'));
        else if (st === 'mocked') toast.warning('Mocked (no key)');
        else toast.error(r?.data?.error || 'Failed');
      } else {
        const r = await axios.post(`${API}/buildings/${buildingId}/collections/${caseDetail.case_id}/pdf`, body, { responseType: 'blob' });
        const url = URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
        window.open(url, '_blank');
        toast.success(label('Δημιουργήθηκε PDF', 'PDF generated'));
      }
      setSendDialog(null);
      openDetail(caseDetail);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };

  // ---- Templates CRUD ----
  const openTplDialog = (tpl) => setTplDialog(tpl ? { ...tpl } : { template_type: 'email', name: '', subject: '', body: '' });

  const saveTpl = async () => {
    if (!tplDialog) return;
    if (!tplDialog.name.trim()) { toast.error('Name required'); return; }
    try {
      if (tplDialog.template_id) {
        await axios.put(`${API}/buildings/${buildingId}/collections/templates/${tplDialog.template_id}`, tplDialog);
      } else {
        await axios.post(`${API}/buildings/${buildingId}/collections/templates`, tplDialog);
      }
      toast.success(label('Αποθηκεύτηκε', 'Saved'));
      setTplDialog(null);
      reload();
    } catch (e) { toast.error('Save failed'); }
  };

  const deleteTpl = async (tpl) => {
    if (!window.confirm(label('Διαγραφή template;', 'Delete template?'))) return;
    try {
      await axios.delete(`${API}/buildings/${buildingId}/collections/templates/${tpl.template_id}`);
      reload();
    } catch (e) { toast.error('Delete failed'); }
  };

  const runSeed = async () => {
    try {
      const r = await axios.post(`${API}/buildings/${buildingId}/collections/_seed`);
      toast.success(`${label('Seeded', 'Seeded')}: ${r.data.seeded_cases}`);
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Seed failed'); }
  };

  const emailTpls = useMemo(() => templates.filter(t => t.template_type === 'email'), [templates]);
  const letterTpls = useMemo(() => templates.filter(t => t.template_type === 'letter'), [templates]);

  return (
    <div className="space-y-4" data-testid="collections-panel">
      {/* Sub-nav */}
      <div className="flex items-center gap-2 border-b border-zinc-200">
        <button onClick={() => setTab('cases')} className={`px-4 py-2 text-sm ${tab === 'cases' ? 'border-b-2 border-zinc-900 font-semibold' : 'text-zinc-500'}`} data-testid="col-tab-cases">
          {label('Υποθέσεις', 'Cases')} ({cases.length})
        </button>
        <button onClick={() => setTab('templates')} className={`px-4 py-2 text-sm ${tab === 'templates' ? 'border-b-2 border-zinc-900 font-semibold' : 'text-zinc-500'}`} data-testid="col-tab-templates">
          {label('Πρότυπα', 'Templates')} ({templates.length})
        </button>
        <div className="flex-1" />
        {isAdmin && buildingId === 'bld_2ee08c12d566' && (
          <Button variant="outline" size="sm" className="rounded-none text-xs" onClick={runSeed} data-testid="col-seed-btn">
            {label('Seed (TEST)', 'Seed (TEST)')}
          </Button>
        )}
      </div>

      {/* CASES TAB */}
      {tab === 'cases' && (
        <div className="space-y-3" data-testid="col-cases-view">
          <div className="flex items-center gap-2">
            <Select value={newCaseAptId} onValueChange={setNewCaseAptId}>
              <SelectTrigger className="rounded-none max-w-xs" data-testid="col-new-apt-select">
                <SelectValue placeholder={label('Επίλεξε διαμέρισμα...', 'Pick apartment...')} />
              </SelectTrigger>
              <SelectContent>
                {apartments.map(a => (
                  <SelectItem key={a.apartment_id} value={a.apartment_id}>
                    {a.apartment_number}{a.owner_name ? ` — ${a.owner_name}` : ''}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button size="sm" onClick={() => openCase(newCaseAptId)} disabled={!newCaseAptId} className="rounded-none bg-zinc-950" data-testid="col-new-case-btn">
              <Plus size={14} className="mr-1" />{label('Νέο Case', 'Open Case')}
            </Button>
            <Button size="sm" variant="outline" onClick={reload} className="rounded-none" data-testid="col-refresh">
              <ArrowClockwise size={14} />
            </Button>
          </div>
          {loading && <div className="text-xs text-zinc-500">Loading…</div>}
          {!loading && cases.length === 0 && (
            <div className="p-6 text-center text-sm text-zinc-500 border border-dashed border-zinc-300">
              {label('Δεν υπάρχουν ενεργές υποθέσεις.', 'No active cases.')}
            </div>
          )}
          {cases.map(c => (
            <div key={c.case_id} className="border border-zinc-200 bg-white p-3 flex items-center justify-between gap-3 hover:bg-zinc-50 cursor-pointer" onClick={() => openDetail(c)} data-testid={`col-case-${c.case_id}`}>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-semibold text-sm">Apt {c.apartment_number || '?'}</span>
                  {c.owner_name && <span className="text-zinc-500 text-xs">{c.owner_name}</span>}
                  <span className={`text-[10px] uppercase px-2 py-0.5 ${STATUS_BADGE_COLOR[c.status] || 'bg-zinc-100 text-zinc-700'}`}>
                    {isEl ? (STATUS_LABEL_EL[c.status] || c.status) : c.status.replace(/_/g, ' ')}
                  </span>
                </div>
                <div className="text-xs text-zinc-500 mt-1 flex items-center gap-3 font-mono">
                  <span><Warning size={11} className="inline mr-1 text-red-500" />€{(c.overdue_total || 0).toFixed(2)}</span>
                  <span>{c.overdue_count || 0} inv</span>
                  <span>{c.max_days_late || 0}d overdue</span>
                </div>
              </div>
              <button onClick={(e) => { e.stopPropagation(); deleteCase(c); }} className="p-1 text-zinc-400 hover:text-red-600" title={label('Κλείσιμο', 'Close')} data-testid={`col-delete-${c.case_id}`}>
                <Trash size={14} />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* TEMPLATES TAB */}
      {tab === 'templates' && (
        <div className="space-y-3" data-testid="col-templates-view">
          <div className="flex items-center justify-between">
            <div className="text-xs text-zinc-500">
              {label('Placeholders:', 'Placeholders:')} <code className="text-[10px]">{'{OwnerName} {ApartmentNumber} {BuildingName} {OutstandingAmount} {OutstandingInvoices} {DaysLate} {TodayDate} {CompanyName} {CompanyPhone} {CompanyEmail} {IBAN}'}</code>
            </div>
            <Button size="sm" onClick={() => openTplDialog(null)} className="rounded-none bg-zinc-950" data-testid="col-new-tpl-btn">
              <Plus size={14} className="mr-1" />{label('Νέο Template', 'New Template')}
            </Button>
          </div>
          {['email', 'letter'].map(ttype => {
            const list = ttype === 'email' ? emailTpls : letterTpls;
            return (
              <div key={ttype} className="space-y-1">
                <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold">
                  {ttype === 'email' ? label('Email Templates', 'Email Templates') : label('Letter Templates', 'Letter Templates')} ({list.length})
                </div>
                {list.map(t => (
                  <div key={t.template_id} className="border border-zinc-200 bg-white px-3 py-2 flex items-center justify-between gap-2" data-testid={`col-tpl-${t.template_id}`}>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium">{t.name}</div>
                      {t.subject && <div className="text-[11px] text-zinc-500 truncate">{t.subject}</div>}
                    </div>
                    <button onClick={() => openTplDialog(t)} className="p-1 text-zinc-400 hover:text-zinc-800" title={label('Επεξεργασία', 'Edit')} data-testid={`col-tpl-edit-${t.template_id}`}>
                      <PencilSimple size={13} />
                    </button>
                    <button onClick={() => deleteTpl(t)} className="p-1 text-zinc-400 hover:text-red-600" title={label('Διαγραφή', 'Delete')} data-testid={`col-tpl-del-${t.template_id}`}>
                      <Trash size={13} />
                    </button>
                  </div>
                ))}
                {list.length === 0 && <div className="text-xs text-zinc-400 italic px-2">— empty —</div>}
              </div>
            );
          })}
        </div>
      )}

      {/* CASE DETAIL DIALOG */}
      <Dialog open={!!selectedCase} onOpenChange={(v) => !v && closeDetail()}>
        <DialogContent className="max-w-3xl rounded-none max-h-[85vh] overflow-y-auto" data-testid="col-case-detail">
          {caseDetail && (
            <>
              <DialogHeader>
                <DialogTitle>
                  Case — Apt {caseDetail.apartment?.apartment_number} · {caseDetail.apartment?.owner_name}
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div className="grid grid-cols-3 gap-3 border-b border-zinc-200 pb-3">
                  <div>
                    <Label className="text-xs">{label('Κατάσταση', 'Status')}</Label>
                    <Select value={caseDetail.status} onValueChange={updateStatus}>
                      <SelectTrigger className="mt-1 rounded-none" data-testid="col-detail-status">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {STATUSES.map(s => (
                          <SelectItem key={s} value={s}>{isEl ? STATUS_LABEL_EL[s] : s.replace(/_/g, ' ')}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="col-span-2 flex flex-col justify-end gap-2">
                    <div className="flex gap-2 flex-wrap">
                      <Button size="sm" variant="outline" onClick={() => openSendDialog('email')} className="rounded-none text-xs" data-testid="col-detail-send-email">
                        <EnvelopeSimple size={13} className="mr-1" />{label('Αποστολή Email', 'Send Email')}
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => openSendDialog('pdf')} className="rounded-none text-xs" data-testid="col-detail-gen-pdf">
                        <FilePdf size={13} className="mr-1" />{label('PDF Επιστολή', 'Generate PDF')}
                      </Button>
                    </div>
                  </div>
                </div>

                {/* Overdue snapshot */}
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold mb-1">
                    {label('Ληξιπρόθεσμα', 'Overdue')} — €{(caseDetail.overdue?.total || 0).toFixed(2)}
                  </div>
                  {(caseDetail.overdue?.invoices || []).length === 0 && (
                    <div className="text-xs text-zinc-400 italic">— {label('καμία', 'none')} —</div>
                  )}
                  {(caseDetail.overdue?.invoices || []).map(inv => (
                    <div key={inv.invoice_id} className="text-xs grid grid-cols-4 gap-2 py-1 border-b border-zinc-100 font-mono" data-testid={`col-detail-overdue-${inv.invoice_id}`}>
                      <span>{inv.period || inv.invoice_number}</span>
                      <span>{inv.due_date}</span>
                      <span className="text-right">€{inv.remaining.toFixed(2)}</span>
                      <span className="text-right text-red-600">{inv.days_late}d</span>
                    </div>
                  ))}
                </div>

                {/* History */}
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold mb-1">
                    {label('Ιστορικό', 'History')} ({history.length})
                  </div>
                  <NoteAdder onSubmit={addNote} language={language} />
                  <div className="space-y-1 mt-2 max-h-64 overflow-y-auto">
                    {history.map(h => (
                      <div key={h.history_id} className="text-xs border-l-2 border-zinc-300 pl-2 py-1" data-testid={`col-hist-${h.history_id}`}>
                        <div className="flex items-center gap-2">
                          <span className="text-[10px] uppercase font-semibold text-zinc-500">{h.entry_type.replace(/_/g, ' ')}</span>
                          <span className="text-[10px] text-zinc-400">{(h.created_at || '').slice(0, 19).replace('T', ' ')}</span>
                          {h.actor && <span className="text-[10px] text-zinc-400">· {h.actor}</span>}
                        </div>
                        {h.subject && <div className="text-zinc-800 mt-0.5 truncate">{h.subject}</div>}
                        {h.body && <div className="text-zinc-600 mt-0.5 whitespace-pre-wrap">{h.body}</div>}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* SEND DIALOG (email / pdf) */}
      <Dialog open={!!sendDialog} onOpenChange={(v) => !v && setSendDialog(null)}>
        <DialogContent className="max-w-lg rounded-none" data-testid="col-send-dialog">
          <DialogHeader>
            <DialogTitle>
              {sendDialog?.mode === 'email' ? label('Αποστολή Email', 'Send Email') : label('Δημιουργία PDF', 'Generate PDF')}
            </DialogTitle>
          </DialogHeader>
          {sendDialog && (
            <div className="space-y-3">
              <div>
                <Label className="text-xs">{label('Template', 'Template')}</Label>
                <Select value={sendDialog.tplId} onValueChange={(v) => setSendDialog({ ...sendDialog, tplId: v })}>
                  <SelectTrigger className="rounded-none" data-testid="col-send-tpl">
                    <SelectValue placeholder={label('Επίλεξε...', 'Pick...')} />
                  </SelectTrigger>
                  <SelectContent>
                    {(sendDialog.mode === 'email' ? emailTpls : letterTpls).map(t => (
                      <SelectItem key={t.template_id} value={t.template_id}>{t.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {sendDialog.mode === 'email' && (
                <div>
                  <Label className="text-xs">{label('Θέμα (override)', 'Subject (override)')}</Label>
                  <Input value={sendDialog.subject} onChange={e => setSendDialog({ ...sendDialog, subject: e.target.value })} className="rounded-none" data-testid="col-send-subject" />
                </div>
              )}
              <div>
                <Label className="text-xs">{label('Σώμα (override)', 'Body (override)')}</Label>
                <Textarea rows={6} value={sendDialog.body} onChange={e => setSendDialog({ ...sendDialog, body: e.target.value })} className="rounded-none text-xs font-mono" data-testid="col-send-body" />
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setSendDialog(null)} className="rounded-none" data-testid="col-send-cancel">
                  {label('Ακύρωση', 'Cancel')}
                </Button>
                <Button onClick={runSend} className="bg-zinc-950 rounded-none" data-testid="col-send-confirm">
                  {sendDialog.mode === 'email' ? label('Αποστολή', 'Send') : label('Δημιουργία PDF', 'Generate')}
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* TEMPLATE EDITOR */}
      <Dialog open={!!tplDialog} onOpenChange={(v) => !v && setTplDialog(null)}>
        <DialogContent className="max-w-xl rounded-none max-h-[85vh] overflow-y-auto" data-testid="col-tpl-dialog">
          <DialogHeader>
            <DialogTitle>{tplDialog?.template_id ? label('Επεξεργασία Template', 'Edit Template') : label('Νέο Template', 'New Template')}</DialogTitle>
          </DialogHeader>
          {tplDialog && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">{label('Τύπος', 'Type')}</Label>
                  <Select value={tplDialog.template_type} onValueChange={(v) => setTplDialog({ ...tplDialog, template_type: v })}>
                    <SelectTrigger className="rounded-none" data-testid="col-tpl-type"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="email">Email</SelectItem>
                      <SelectItem value="letter">Letter</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">{label('Όνομα', 'Name')}</Label>
                  <Input value={tplDialog.name} onChange={e => setTplDialog({ ...tplDialog, name: e.target.value })} className="rounded-none" data-testid="col-tpl-name" />
                </div>
              </div>
              {tplDialog.template_type === 'email' && (
                <div>
                  <Label className="text-xs">{label('Θέμα', 'Subject')}</Label>
                  <Input value={tplDialog.subject || ''} onChange={e => setTplDialog({ ...tplDialog, subject: e.target.value })} className="rounded-none font-mono text-xs" data-testid="col-tpl-subject" />
                </div>
              )}
              <div>
                <Label className="text-xs">{label('Σώμα', 'Body')}</Label>
                <Textarea rows={12} value={tplDialog.body || ''} onChange={e => setTplDialog({ ...tplDialog, body: e.target.value })} className="rounded-none font-mono text-xs" data-testid="col-tpl-body" />
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setTplDialog(null)} className="rounded-none" data-testid="col-tpl-cancel">
                  {label('Ακύρωση', 'Cancel')}
                </Button>
                <Button onClick={saveTpl} className="bg-zinc-950 rounded-none" data-testid="col-tpl-save">
                  {label('Αποθήκευση', 'Save')}
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Small inline note adder — kept local to avoid extra state hoist.
function NoteAdder({ onSubmit, language }) {
  const [text, setText] = useState('');
  return (
    <div className="flex gap-2">
      <Input
        value={text}
        onChange={e => setText(e.target.value)}
        placeholder={language === 'el' ? 'Νέα σημείωση...' : 'New note...'}
        className="rounded-none text-xs"
        data-testid="col-note-input"
        onKeyDown={(e) => { if (e.key === 'Enter') { onSubmit(text); setText(''); } }}
      />
      <Button size="sm" onClick={() => { onSubmit(text); setText(''); }} className="rounded-none bg-zinc-950" data-testid="col-note-add">
        <Plus size={13} />
      </Button>
    </div>
  );
}
